import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { helpdeskEmailLogsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Mail,
  AlertCircle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';

const EmailLogs = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allLogs, setAllLogs] = useState([]);
  const [failedLogs, setFailedLogs] = useState([]);
  const [searchTicket, setSearchTicket] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Admin de módulo: admin global o miembro del Equipo de Soporte (TI)
  const isAdmin = user?.role === 'admin' || user?.helpdesk_is_agent === true;

  useEffect(() => {
    if (isAdmin) {
      fetchLogs();
    }
  }, [isAdmin]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const [allRes, failedRes] = await Promise.all([
        helpdeskEmailLogsAPI.getAll({ limit: 100 }),
        helpdeskEmailLogsAPI.getFailed({ limit: 50 }),
      ]);
      setAllLogs(allRes.data);
      setFailedLogs(failedRes.data);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      toast.error('Error al cargar los logs de email');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLogs();
    setRefreshing(false);
    toast.success('Logs actualizados');
  };

  const handleSearch = async () => {
    if (!searchTicket.trim()) {
      toast.error('Ingresa un ID de ticket');
      return;
    }
    setLoading(true);
    try {
      const res = await helpdeskEmailLogsAPI.getByTicket(searchTicket.trim());
      setAllLogs(res.data);
    } catch (error) {
      console.error('Error searching logs:', error);
      toast.error('Error al buscar logs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Enviado</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" /> Fallido</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Pendiente</Badge>;
      case 'retrying':
        return <Badge className="bg-blue-100 text-blue-700"><RefreshCw className="w-3 h-3 mr-1" /> Reintentando</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getEventLabel = (event) => {
    const labels = {
      'ticket_created': 'Ticket Creado (Admin)',
      'ticket_created_confirmation': 'Confirmación (Usuario)',
      'ticket_assigned': 'Asignación',
      'ticket_reassigned': 'Reasignación',
      'ticket_closed': 'Cierre',
      'ticket_updated': 'Actualización',
    };
    return labels[event] || event;
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      return new Date(isoString).toLocaleString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return isoString;
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Acceso Denegado</h2>
          <p className="text-slate-500 mt-2">No tienes permisos para ver esta página.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="email-logs-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-3">
              <Mail className="w-7 h-7 text-blue-600" />
              Logs de Email
            </h1>
            <p className="text-slate-500 mt-1">
              Historial de correos enviados por el sistema de Mesa de Ayuda
            </p>
          </div>
          <Button 
            onClick={handleRefresh} 
            variant="outline"
            disabled={refreshing}
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Buscar por ID de ticket..."
                  value={searchTicket}
                  onChange={(e) => setSearchTicket(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="search-ticket-input"
                />
              </div>
              <Button onClick={handleSearch} data-testid="search-logs-btn">
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </Button>
              <Button 
                variant="outline" 
                onClick={fetchLogs}
              >
                Ver Todos
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs Tabs */}
        <Tabs defaultValue="all" className="space-y-4">
          <TabsList>
            <TabsTrigger value="all" data-testid="all-logs-tab">
              Todos los Envíos ({allLogs.length})
            </TabsTrigger>
            <TabsTrigger value="failed" data-testid="failed-logs-tab">
              <AlertCircle className="w-4 h-4 mr-1 text-red-500" />
              Fallidos ({failedLogs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Historial de Envíos</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : allLogs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No hay logs de email disponibles
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Evento</TableHead>
                        <TableHead>Destinatario</TableHead>
                        <TableHead>Asunto</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Reintentos</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allLogs.map((log) => (
                        <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                          <TableCell className="text-sm text-slate-600">
                            {formatDate(log.created_at)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-normal">
                              {getEventLabel(log.event_type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{log.to}</TableCell>
                          <TableCell className="text-sm max-w-xs truncate">
                            {log.subject || 'N/A'}
                          </TableCell>
                          <TableCell>{getStatusBadge(log.status)}</TableCell>
                          <TableCell className="text-center">
                            {log.retry_count || 0}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="failed">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Envíos Fallidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : failedLogs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                    No hay envíos fallidos. ¡Todo está funcionando correctamente!
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Ticket</TableHead>
                        <TableHead>Evento</TableHead>
                        <TableHead>Destinatario</TableHead>
                        <TableHead>Error</TableHead>
                        <TableHead>Reintentos</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {failedLogs.map((log) => (
                        <TableRow key={log.id} className="bg-red-50/50">
                          <TableCell className="text-sm text-slate-600">
                            {formatDate(log.created_at)}
                          </TableCell>
                          <TableCell className="text-sm font-mono">
                            {log.ticket_id?.slice(0, 8)}...
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-normal">
                              {getEventLabel(log.event_type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{log.to}</TableCell>
                          <TableCell className="text-sm text-red-600 max-w-xs truncate">
                            {log.error || 'Error desconocido'}
                          </TableCell>
                          <TableCell className="text-center">
                            {log.retry_count || 0}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default EmailLogs;
